/**
 * Team Access page — workspace members and invites.
 *
 * Lists current members with roles, pending invites, and an invite form
 * for owners.
 */

import { useState, useEffect, useCallback } from 'react';
import { useWorkspace } from '../hooks/useWorkspace';
import * as api from '../api-client';
import {
  AdminEmptyState,
  AdminPage,
  AdminPageHeader,
  AdminPanel,
} from '../components/AdminPrimitives';

export function TeamAccessPage() {
  const { workspace } = useWorkspace();
  const [members, setMembers] = useState<api.MemberResponse[]>([]);
  const [invites, setInvites] = useState<api.InviteResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Invite form
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [inviting, setInviting] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);

  const loadMembers = useCallback(async () => {
    if (!workspace) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await api.listMembers(workspace.workspace_key);
      setMembers(res.members);
      setInvites(res.pending_invites);
    } catch (err) {
      setError(err instanceof api.AdminApiError ? err.detail : 'Failed to load team');
    } finally {
      setIsLoading(false);
    }
  }, [workspace]);

  useEffect(() => {
    void loadMembers();
  }, [loadMembers]);

  const handleInvite = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!workspace || !inviteEmail.trim()) return;
      setInviting(true);
      setInviteSuccess(null);
      setError(null);
      try {
        await api.inviteMember(workspace.workspace_key, inviteEmail.trim(), inviteRole);
        setInviteSuccess(`Invite sent to ${inviteEmail.trim()}`);
        setInviteEmail('');
        // Refresh the list to show the new invite
        await loadMembers();
      } catch (err) {
        setError(err instanceof api.AdminApiError ? err.detail : 'Failed to send invite');
      } finally {
        setInviting(false);
      }
    },
    [workspace, inviteEmail, inviteRole, loadMembers],
  );

  if (!workspace) return null;

  const canInvite = workspace.role === 'owner';

  return (
    <AdminPage className="max-w-4xl">
      <AdminPageHeader
        title="Team Access"
        subtitle="Manage workspace members, pending invitations, and role assignments."
      />

      {error && (
        <div role="alert" className="mb-4 p-3 text-sm rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-[var(--status-error)]">
          {error}
        </div>
      )}

      {inviteSuccess && (
        <div role="status" className="mb-4 p-3 text-sm rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-[var(--status-success)]">
          {inviteSuccess}
        </div>
      )}

      {canInvite && (
        <AdminPanel
          title="Invite a Team Member"
          subtitle="Workspace owners can invite viewers or additional owners in v1."
          className="mb-6"
        >
          <form
            onSubmit={handleInvite}
            className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_180px_auto] lg:items-end"
          >
            <label className="min-w-0">
              <span className="sr-only">Email address</span>
              <input
                type="email"
                required
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="email@example.com"
                className="form-input w-full min-w-0"
                disabled={inviting}
              />
            </label>
            <label className="min-w-0">
              <span className="sr-only">Role</span>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="form-input w-full min-w-0"
                disabled={inviting}
              >
                <option value="viewer">Viewer</option>
                <option value="owner">Owner</option>
              </select>
            </label>
            <button
              type="submit"
              disabled={inviting || !inviteEmail.trim()}
              className="w-full px-4 py-2.5 rounded-lg bg-[var(--accent-primary)] text-white font-medium text-sm hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity whitespace-nowrap lg:w-auto"
            >
              {inviting ? 'Sending...' : 'Send invite'}
            </button>
          </form>
        </AdminPanel>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 rounded-[1.2rem] bg-[var(--surface-tertiary)] animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          <AdminPanel title={`Members (${members.length})`} subtitle="People who can currently access this workspace." className="mb-6" contentClassName="p-0">
            {members.length === 0 ? (
              <div className="p-5">
                <AdminEmptyState
                  title="No members yet"
                  description="Workspace owners can invite teammates to give them access to this workspace."
                />
              </div>
            ) : (
              <div className="divide-y divide-[var(--border-subtle)]">
                {members.map((m) => (
                  <div key={m.user_id} className="flex items-center gap-3 bg-white/70 px-5 py-4">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--surface-tertiary)] text-xs font-semibold text-[var(--accent-primary)]">
                      {(m.email[0] ?? '?').toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[var(--text-primary)] truncate">
                        {m.display_name ?? m.email}
                      </p>
                      <p className="text-xs text-[var(--text-tertiary)]">{m.email}</p>
                    </div>
                    <RoleBadge role={m.role} />
                  </div>
                ))}
              </div>
            )}
          </AdminPanel>

          {invites.length > 0 && (
            <AdminPanel
              title={`Pending Invites (${invites.length})`}
              subtitle="Invitations that have been sent but not yet accepted."
              contentClassName="p-0"
            >
              <div className="divide-y divide-[var(--border-subtle)]">
                {invites.map((inv) => (
                  <div key={inv.id} className="flex items-center gap-3 bg-white/70 px-5 py-4">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--surface-tertiary)]">
                      <MailIcon className="w-4 h-4 text-[var(--text-tertiary)]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[var(--text-primary)] truncate">
                        {inv.email}
                      </p>
                      <p className="text-xs text-[var(--text-tertiary)]">
                        Invited {formatDate(inv.created_at)} &middot; Expires {formatDate(inv.expires_at)}
                      </p>
                    </div>
                    <RoleBadge role={inv.role} />
                  </div>
                ))}
              </div>
            </AdminPanel>
          )}

          <section className="mt-6 rounded-[1.5rem] border border-dashed border-[var(--border-default)] bg-[var(--surface-tertiary)] px-5 py-5 shadow-[0_8px_20px_rgba(15,23,42,0.03)]">
            <div className="max-w-3xl">
              <p className="text-base font-semibold text-[var(--text-primary)]">Matcora Support Access</p>
              <p className="mt-2 text-sm leading-6 text-[var(--text-secondary)]">
                Platform-level support access is managed by Matcora and is not shown in the customer member roster.
              </p>
              <div className="mt-4 rounded-[1rem] border border-[var(--border-default)] bg-[rgba(255,255,255,0.72)] px-4 py-4 text-sm leading-6 text-[var(--text-secondary)]">
                Authorized Matcora administrators may access workspace data for support, reliability, and product improvement.
              </div>
            </div>
          </section>
        </>
      )}
    </AdminPage>
  );
}

// ── Components ────────────────────────────────────────────────────────

function RoleBadge({ role }: { role: string }) {
  const colors: Record<string, string> = {
    owner: 'border-[rgba(15,23,42,0.12)] bg-[rgba(15,23,42,0.08)] text-[var(--text-primary)]',
    editor: 'border-[rgba(47,93,138,0.18)] bg-[rgba(47,93,138,0.10)] text-[var(--accent-visual)]',
    viewer: 'border-[var(--border-default)] bg-[var(--surface-tertiary)] text-[var(--text-secondary)]',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold uppercase tracking-[0.12em] ${colors[role] ?? colors.viewer}`}
    >
      {role}
    </span>
  );
}

function MailIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 7l-10 7L2 7" />
    </svg>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}
